import React, { FC, useContext, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { Container } from '../../Container';
import { Typography } from '../../Typography';
import { Button, ButtonProps } from '../../Button';
import { createMarkup } from '../../../utils';
import { HeroComponentRenderer } from './HeroComponentRenderer';
import { Img } from '../../Img';
import { TrustpilotStars } from '../../TrustpilotStars';
import { UiStateContext } from '../../../context/UiStateContext';
import { GoogleStars } from '../../GoogleStars';
import { useNavigation } from '../../../context/NavigationContext';
import { StampDutyCalculatorWithBreakdown } from '../../calculators/StampDutyCalculatorWithBreakdown';
import { Spacer } from '../../Spacer';
import { useExperiments } from '../../../context/ExperimentsContext';

export interface HeroProps {
  heading: string;
  subHeading?: string;
  lead?: string;
  button?: ButtonProps;
  backgroundImage?: any;
  mobileBackgroundImage?: any;
  tabletBackgroundImage?: any;
  trustpilot?: boolean;
  component?: any;
  showReviewCount?: boolean;
  googleReviewsLink?: string;
  googleReviewsRating?: number;
  customComponent?: React.ReactNode;
  hasReviewPanel?: boolean; // Specici prop used for branches to control the visibility of review stars between desktop/mobile
}

interface StyledHeaderProps {
  hasComponent: boolean;
  isBrandMessage?: boolean;
  componentHiddenOnDesktop?: boolean;
}

interface StyledLeadProps {
  hideLeadOnMobile?: boolean;
  isBrandMessage?: boolean;
}

const StyledHero = styled.div`
  ${({ theme: { space, media, global, layers } }) => css`
    position: relative;
    overflow: hidden;

    &:before {
      content: '';
      background-image: linear-gradient(90deg, #111111 0%, rgba(17, 17, 17, 0) 100%);
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      z-index: ${layers.heroGradient};
    }

    // Background image

    .gatsby-image-wrapper {
      position: absolute;
      left: 0;
      top: 0;
      z-index: ${layers.heroBackgroundImage};
      height: 100%;
      width: 100%;
    }

    // Tablet > styles

    @media (min-width: ${media.xl}) {
      margin: 0 ${space.lg};
      border-radius: ${global.borderRadius.default};

      &:before {
        opacity: 0.9;
        width: 75%;
      }
    }
  `};
`;

const StyledContainer = styled(Container)`
  ${({ theme: { layers } }) => css`
    position: relative;
    z-index: ${layers.heroContainer};
  `};
`;

const StyledHeader = styled.div<StyledHeaderProps>`
  ${({ theme: { space, colors, media }, hasComponent, isBrandMessage, componentHiddenOnDesktop }) => css`
    max-width: ${isBrandMessage ? '300px' : '575px'};
    padding: ${hasComponent
      ? `${space.lg} ${space.md} calc(${space.xxl} + ${space.lg}) ${space.md}`
      : `${space.lg} ${space.md}`};
    color: ${colors.white.default};

    @media (min-width: ${media.md}) {
      max-width: ${isBrandMessage ? '340px' : '575px'};
    }

    // Tablet > styles

    @media (min-width: ${media.xl}) {
      max-width: 575px;
      padding: ${space.xl} 0;
      padding: ${hasComponent && !componentHiddenOnDesktop
        ? `${space.xl} 0 calc(${space.xxxl} + ${space.lg}) 0`
        : `${space.xl} 0`};
      min-height: 496px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  `};
`;

const StyledSubHeading = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.lg};
  `};
`;

const StyledLead = styled(Typography)<StyledLeadProps>`
  ${({ theme: { space, maxMedia }, hideLeadOnMobile, isBrandMessage }) => css`
    margin-top: ${space.md};
    max-width: ${isBrandMessage ? '460px' : '575px'};
    // Tablet < styles

    @media (max-width: ${maxMedia.xl}) {
      display: ${hideLeadOnMobile ? 'none' : 'block'};
    }
  `};
`;

const StyledStars = styled.div<{ hiddenMobile?: boolean }>`
  ${({ theme: { space, maxMedia }, hiddenMobile }) => css`
    margin-top: ${space.lg};
    display: flex;
    gap: ${space.lg};

    // Mobile < styles
    ${hiddenMobile &&
    css`
      @media (max-width: ${maxMedia.sm}) {
        display: none;
      }
    `}
  `};
`;

const StyledButton = styled.div`
  ${({ theme: { space } }) => css`
    margin-top: ${space.lg};
  `};
`;

const StyledHeroComponent = styled.div<{ hiddenDeskop?: boolean }>`
  ${({ theme: { space, layers, media }, hiddenDeskop }) => css`
    margin-top: -${space.xl};
    position: relative;
    z-index: ${layers.heroComponent};

    // Tablet > styles
    ${hiddenDeskop &&
    css`
      @media (min-width: ${media.sm}) {
        display: none;
      }
    `}
  `};
`;

// Images

const StyledMobileImage = styled(Img)`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.md}) {
      display: none !important;
    }
  `};
`;

const StyledTabletImage = styled(Img)`
  ${({ theme: { media, maxMedia } }) => css`
    @media (max-width: ${maxMedia.md}) {
      display: none !important;
    }

    @media (min-width: ${media.xl}) {
      display: none !important;
    }
  `};
`;

const StyledDesktopImage = styled(Img)`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.xl}) {
      display: none !important;
    }
  `};
`;

const StyledBackButton = styled.div`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.lg};
    display: inline-flex;
    height: 38px;
  `};
`;

export const Hero: FC<HeroProps> = ({
  heading,
  subHeading,
  lead,
  button,
  backgroundImage,
  mobileBackgroundImage,
  tabletBackgroundImage,
  component,
  trustpilot,
  showReviewCount,
  googleReviewsLink,
  googleReviewsRating,
  customComponent,
  hasReviewPanel,
  ...rest
}) => {
  const { setHeroOutOfView } = useContext(UiStateContext);
  const { experiments } = useExperiments();
  const heroRef = useRef(null);
  const { location } = useNavigation();

  // Check if KFH25004 experiment is active
  // This experiment tests showing stamp duty calculator in hero for stamp duty calculator page
  const isKFH25004Variation = experiments?.KFH25004 === 'variation';

  const { settings } = useStaticQuery(graphql`
    query HeroSettingsQuery {
      settings: kfhKfhSettingsType {
        trustPilotScore {
          total
          stars
        }
      }
    }
  `);

  useEffect(() => {
    const checkIfInView = () => {
      const heroElement = heroRef.current;

      if (heroElement) {
        const { top, bottom } = heroElement.getBoundingClientRect();
        const isInViewport = top < window.innerHeight && bottom >= 0;

        if (isInViewport) {
          // Element is in the viewport
          setHeroOutOfView(false);
        } else {
          // Element is not in the viewport
          setHeroOutOfView(true);
        }
      }
    };

    // Check on initial render
    checkIfInView();

    // Attach the scroll event listener
    window.addEventListener('scroll', checkIfInView);

    // Clean up the listener when the component is unmounted
    return () => {
      window.removeEventListener('scroll', checkIfInView);
    };
  }, [setHeroOutOfView]);

  return (
    <>
      <StyledHero {...rest}>
        {backgroundImage && !mobileBackgroundImage && <Img src={backgroundImage} loading="eager" />}
        {mobileBackgroundImage && <StyledMobileImage src={mobileBackgroundImage} loading="eager" />}
        {tabletBackgroundImage && <StyledTabletImage src={tabletBackgroundImage} loading="eager" />}
        {backgroundImage && mobileBackgroundImage && <StyledDesktopImage src={backgroundImage} loading="eager" />}
        <StyledContainer>
          <StyledHeader
            hasComponent={!!component || !!customComponent}
            componentHiddenOnDesktop={hasReviewPanel}
            isBrandMessage={heading.includes('London. Property')}
          >
            <StyledBackButton>
              {location?.state?.fromSearchResults && (
                <Button variant="pillOutline" arrowLeft onClick={() => window.history.back()}>
                  Back to search results
                </Button>
              )}
            </StyledBackButton>

            <Typography component="h1" variant="displayLarge">
              {createMarkup(heading)}
            </Typography>
            {subHeading && (
              <StyledSubHeading component="p" variant="titleLarge">
                {subHeading}
              </StyledSubHeading>
            )}
            {lead && (
              <StyledLead
                component="p"
                variant="lead"
                hideLeadOnMobile={trustpilot}
                isBrandMessage={heading.includes('London. Property')}
              >
                {createMarkup(lead)}
              </StyledLead>
            )}
            {trustpilot || googleReviewsRating ? (
              <StyledStars hiddenMobile={hasReviewPanel}>
                <TrustpilotStars
                  showReviewCount={showReviewCount}
                  inverse
                  totalReviews={settings.trustPilotScore.total.toLocaleString()}
                  rating={settings.trustPilotScore.stars}
                  url="https://uk.trustpilot.com/review/www.kfh.co.uk"
                />
                {googleReviewsRating && googleReviewsLink && (
                  <GoogleStars url={googleReviewsLink} rating={googleReviewsRating} inverse />
                )}
              </StyledStars>
            ) : (
              ''
            )}
            {button && (
              <StyledButton>
                <Button arrowRight {...button} />
              </StyledButton>
            )}
          </StyledHeader>
        </StyledContainer>
      </StyledHero>

      {component && (
        <StyledHeroComponent ref={heroRef}>
          <Container>
            {location.pathname === '/stamp-duty-calculator/' && isKFH25004Variation ? (
              <Spacer spacing="half">
                <StampDutyCalculatorWithBreakdown isHeroComponent />
                <Typography variant="bodySmall">
                  The Stamp Duty Calculator is applied to purchases over £125,000 and the rate varies from 5% to 12%
                  based on purchase price, date and whether you’re a first time buyer or own multiple homes. For non-UK
                  residents, a 2% surcharge is added to each of these rates.
                </Typography>
              </Spacer>
            ) : (
              <HeroComponentRenderer component={component} />
            )}
          </Container>
        </StyledHeroComponent>
      )}

      {customComponent && (
        <StyledHeroComponent ref={heroRef} hiddenDeskop={hasReviewPanel}>
          <Container>{customComponent}</Container>
        </StyledHeroComponent>
      )}
    </>
  );
};

export const HeroQuery = graphql`
  fragment Hero on KFH_UmbracoHeroType {
    title
    lead
    trustpilot
    mobileImage {
      ...HeroMobileImage
    }
    tabletImage {
      ...HeroImage
    }
    image {
      ...HeroImage
    }
    component {
      ...HeroComponent
    }
  }
`;
